import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type ModuleWithDefault<T extends ComponentType<any>> = { default: T };

type Importer<T extends ComponentType<any>> = () => Promise<ModuleWithDefault<T>>;

const RECOVERABLE_IMPORT_ERROR = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;
const RETRY_DELAY_MS = 1000;
const MAX_IMPORT_ATTEMPTS = 2;
const RELOAD_MARKER_PREFIX = 'lazy-recovery-reloaded';
const RELOAD_SEARCH_PARAM = '__lazy_retry';
const TIMESTAMP_SEARCH_PARAM = '__retry';
const MAX_AUTO_RELOADS = 1;
const RELOAD_MARKER_TTL_MS = 30_000;

function isRecoverableImportError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return RECOVERABLE_IMPORT_ERROR.test(message);
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getReloadMarkerKey() {
  return `${RELOAD_MARKER_PREFIX}:${window.location.pathname}`;
}

function readSessionReloadMarker() {
  if (typeof window === 'undefined') return false;

  try {
    const rawValue = window.sessionStorage.getItem(getReloadMarkerKey());

    if (!rawValue) return false;
    if (rawValue === '1') return true;

    const parsedValue = JSON.parse(rawValue) as { expiresAt?: number } | null;

    if (parsedValue?.expiresAt && parsedValue.expiresAt > Date.now()) {
      return true;
    }

    window.sessionStorage.removeItem(getReloadMarkerKey());
    return false;
  } catch {
    return false;
  }
}

function writeSessionReloadMarker() {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(
      getReloadMarkerKey(),
      JSON.stringify({ expiresAt: Date.now() + RELOAD_MARKER_TTL_MS }),
    );
  } catch {
    // Ignore storage access failures in sandboxed/embedded previews.
  }
}

function clearSessionReloadMarker() {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(getReloadMarkerKey());
  } catch {
    // Ignore storage access failures in sandboxed/embedded previews.
  }
}

function getUrlReloadCount() {
  if (typeof window === 'undefined') return 0;

  const currentUrl = new URL(window.location.href);
  const retryCount = Number(currentUrl.searchParams.get(RELOAD_SEARCH_PARAM) ?? '0');
  return Number.isFinite(retryCount) ? retryCount : 0;
}

function shouldForceReload(cacheKey: string) {
  if (typeof window === 'undefined') return false;

  const alreadyReloadedInSession = readSessionReloadMarker();
  const urlReloadCount = getUrlReloadCount();

  if (alreadyReloadedInSession || urlReloadCount >= MAX_AUTO_RELOADS) {
    return false;
  }

  writeSessionReloadMarker();
  return true;
}

function clearReloadMarker() {
  if (typeof window === 'undefined' || getUrlReloadCount() > 0) return;
  clearSessionReloadMarker();
}

function reloadCurrentPage() {
  if (typeof window === 'undefined') return;

  const retryUrl = new URL(window.location.href);
  const nextRetryCount = getUrlReloadCount() + 1;

  retryUrl.searchParams.set(RELOAD_SEARCH_PARAM, nextRetryCount.toString());
  retryUrl.searchParams.set(TIMESTAMP_SEARCH_PARAM, Date.now().toString());
  window.location.replace(retryUrl.toString());
}

export function lazyWithRecovery<T extends ComponentType<any>>(
  cacheKey: string,
  importer: Importer<T>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= MAX_IMPORT_ATTEMPTS; attempt += 1) {
      try {
        const module = await importer();
        clearReloadMarker();
        return module;
      } catch (error) {
        lastError = error;

        if (!isRecoverableImportError(error)) {
          throw error;
        }

        if (shouldForceReload(cacheKey)) {
          reloadCurrentPage();
          return new Promise<ModuleWithDefault<T>>(() => undefined);
        }

        if (attempt === MAX_IMPORT_ATTEMPTS) {
          throw error;
        }

        await wait(RETRY_DELAY_MS);
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to load lazy component.');
  });
}
